import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { apiFetch } from "@/lib/api-fetch";
import {
  Check,
  X,
  AlertTriangle,
  Inbox,
  RefreshCw,
  MapPin,
  CalendarDays,
  StickyNote,
  Volume2,
  VolumeX,
  Sparkles,
  Zap,
  Clock,
  PauseCircle,
} from "lucide-react";

type LineItem = {
  product_name: string | null;
  size_category: string | null;
  size_category_name: string | null;
  quantity: number | null;
  quantity_type: string | null;
};

type IntakeOrder = {
  id: number;
  wo_number: string;
  product_name: string | null;
  size_category: string | null;
  size_category_name: string | null;
  quantity: number | null;
  quantity_type: string | null;
  client_name: string | null;
  source_channel: string | null;
  source_order_number: string | null;
  accept_deadline: string | null;
  created_at: string;
  priority: string | null;
  status: string | null;
  fulfillment_method: string | null;
  needs_transportation: boolean | null;
  destination_address: string | null;
  destination_city: string | null;
  destination_state: string | null;
  destination_zip: string | null;
  preferred_delivery_date: string | null;
  preferred_delivery_time: string | null;
  proposed_delivery_date: string | null;
  proposed_delivery_time: string | null;
  schedule_note: string | null;
  custom_notes: string | null;
  line_items: LineItem[];
  seconds_remaining: number | null;
  is_overdue: boolean;
};

type CountdownTone = "calm" | "warning" | "danger";
type StatTone = "emerald" | "red" | "amber" | "gray";

const SOURCE_META: Record<
  string,
  { label: string; short: string; bg: string; text: string; border: string; dot: string }
> = {
  mos: {
    label: "Sales Portal",
    short: "Sales",
    bg: "bg-emerald-100 dark:bg-emerald-900/30",
    text: "text-emerald-800 dark:text-emerald-300",
    border: "border-l-emerald-500",
    dot: "bg-emerald-500",
  },
  osw: {
    label: "OSW (Customer)",
    short: "OSW",
    bg: "bg-blue-100 dark:bg-blue-900/30",
    text: "text-blue-800 dark:text-blue-300",
    border: "border-l-blue-500",
    dot: "bg-blue-500",
  },
  cd: {
    label: "CompostDev",
    short: "CD",
    bg: "bg-gray-100 dark:bg-gray-800",
    text: "text-gray-700 dark:text-gray-300",
    border: "border-l-gray-400",
    dot: "bg-gray-400",
  },
  social: {
    label: "Social",
    short: "Social",
    bg: "bg-purple-100 dark:bg-purple-900/30",
    text: "text-purple-800 dark:text-purple-300",
    border: "border-l-purple-500",
    dot: "bg-purple-500",
  },
};

const ACCEPT_WINDOW_SECONDS = 15 * 60;

function formatCountdown(seconds: number | null): { text: string; tone: CountdownTone } {
  if (seconds == null) return { text: "—", tone: "calm" };
  if (seconds < 0) {
    const late = Math.abs(seconds);
    const m = Math.floor(late / 60);
    return { text: `${m}m late`, tone: "danger" };
  }
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  const text = `${m}:${String(s).padStart(2, "0")}`;
  if (seconds <= 180) return { text, tone: "danger" };
  if (seconds <= 420) return { text, tone: "warning" };
  return { text, tone: "calm" };
}

function formatAge(seconds: number): string {
  if (!seconds || seconds < 0) return "—";
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60);
  if (m < 60) return `${m}m`;
  return `${Math.floor(m / 60)}h ${m % 60}m`;
}

function timeOfDayGreeting() {
  const h = new Date().getHours();
  if (h < 12) return "Good morning.";
  if (h < 17) return "Good afternoon.";
  return "Good evening.";
}

function ProgressRing({ seconds, tone }: { seconds: number | null; tone: CountdownTone }) {
  const total = ACCEPT_WINDOW_SECONDS;
  const remaining = seconds == null ? total : Math.max(0, Math.min(total, seconds));
  const pct = remaining / total;
  const r = 18;
  const c = 2 * Math.PI * r;
  const stroke =
    tone === "danger" ? "#dc2626" : tone === "warning" ? "#d97706" : "#059669";
  return (
    <svg width="44" height="44" className="-rotate-90">
      <circle cx="22" cy="22" r={r} fill="none" stroke="currentColor" strokeWidth="3" className="text-gray-200 dark:text-gray-700" />
      <circle
        cx="22"
        cy="22"
        r={r}
        fill="none"
        stroke={stroke}
        strokeWidth="3"
        strokeDasharray={c}
        strokeDashoffset={c * (1 - pct)}
        strokeLinecap="round"
      />
    </svg>
  );
}

export default function OrderIntakeQueue() {
  const { token } = useAuth();
  const [orders, setOrders] = useState<IntakeOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actingId, setActingId] = useState<number | null>(null);
  const [exiting, setExiting] = useState<Set<number>>(new Set());
  const [activeSource, setActiveSource] = useState<string>("all");
  const [soundOn, setSoundOn] = useState(true);
  const [toast, setToast] = useState<{ tone: "success" | "error" | "info"; text: string } | null>(null);
  const [modal, setModal] = useState<null | { type: "confirm" | "propose" | "hold"; order: IntakeOrder }>(null);
  const knownIds = useRef<Set<number>>(new Set());
  const firstLoad = useRef(true);

  const load = useCallback(async () => {
    if (!token) return;
    try {
      const res = await apiFetch("/api/ops/intake-queue", {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Failed to load (${res.status})`);
      const data = await res.json();
      const next: IntakeOrder[] = data.orders || [];
      if (!firstLoad.current && soundOn) {
        const fresh = next.filter((o) => !knownIds.current.has(o.id));
        if (fresh.length) {
          try {
            const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
            const o = ctx.createOscillator();
            const g = ctx.createGain();
            o.connect(g);
            g.connect(ctx.destination);
            o.frequency.value = 880;
            g.gain.value = 0.04;
            o.start();
            o.stop(ctx.currentTime + 0.12);
          } catch {}
        }
      }
      knownIds.current = new Set(next.map((o) => o.id));
      firstLoad.current = false;
      setOrders(next);
      setError(null);
    } catch (e: any) {
      setError(e?.message || "Failed to load queue");
    } finally {
      setLoading(false);
    }
  }, [token, soundOn]);

  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => {
    const tick = setInterval(() => {
      setOrders((prev) =>
        prev.map((o) => {
          if (!o.accept_deadline) return o;
          const remaining = Math.floor((new Date(o.accept_deadline).getTime() - Date.now()) / 1000);
          return { ...o, seconds_remaining: remaining, is_overdue: remaining < 0 };
        })
      );
    }, 1000);
    return () => clearInterval(tick);
  }, []);

  const visible = useMemo(() => {
    if (activeSource === "all") return orders;
    return orders.filter((o) => (o.source_channel || "cd") === activeSource);
  }, [orders, activeSource]);

  const stats = useMemo(() => {
    const pending = orders.length;
    const overdue = orders.filter((o) => o.is_overdue).length;
    const ages = orders.map((o) => Math.floor((Date.now() - new Date(o.created_at).getTime()) / 1000));
    const oldestAge = ages.length ? Math.max(...ages) : 0;
    return { pending, overdue, oldestAge };
  }, [orders]);

  const sourceCounts = useMemo(() => {
    const counts: Record<string, number> = { all: orders.length, mos: 0, osw: 0, cd: 0, social: 0 };
    for (const o of orders) {
      const key = o.source_channel || "cd";
      counts[key] = (counts[key] || 0) + 1;
    }
    return counts;
  }, [orders]);

  const removeOrderWithAnimation = useCallback((orderId: number) => {
    setExiting((prev) => new Set(prev).add(orderId));
    setTimeout(() => {
      setOrders((prev) => prev.filter((o) => o.id !== orderId));
      setExiting((prev) => {
        const next = new Set(prev);
        next.delete(orderId);
        return next;
      });
    }, 280);
  }, []);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 3500);
    return () => clearTimeout(t);
  }, [toast]);

  if (loading) {
    return (
      <div className="p-6 space-y-3">
        <div className="h-8 w-64 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
        <div className="h-24 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-emerald-100 dark:bg-emerald-900/40 flex items-center justify-center">
            <Inbox className="w-5 h-5 text-emerald-700 dark:text-emerald-400" />
          </div>
          <div>
            <h1 className="text-xl md:text-2xl font-bold text-gray-900 dark:text-gray-50 leading-tight">
              Fulfillment Intake
            </h1>
            <p className="text-xs md:text-sm text-gray-500 dark:text-gray-400">
              Confirm schedule · Propose new window · Hold if blocked
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setSoundOn((s) => !s)}
            className="p-2 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition"
          >
            {soundOn ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
          </button>
          <button
            onClick={() => load()}
            className="flex items-center gap-2 px-3 py-2 text-sm bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 transition"
          >
            <RefreshCw className="w-4 h-4" />
            <span className="hidden md:inline">Refresh</span>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3 mb-4">
        <StatCard label="Needs action" value={stats.pending} tone="emerald" />
        <StatCard label="Overdue" value={stats.overdue} tone={stats.overdue > 0 ? "red" : "gray"} />
        <StatCard label="Oldest" value={formatAge(stats.oldestAge)} tone={stats.oldestAge > 600 ? "amber" : "gray"} />
      </div>

      <div className="flex items-center gap-2 mb-4 overflow-x-auto pb-1">
        {(["all", "mos", "osw", "cd", "social"] as const).map((key) => {
          const meta = key === "all" ? null : SOURCE_META[key];
          const count = sourceCounts[key] ?? 0;
          const active = activeSource === key;
          return (
            <button
              key={key}
              onClick={() => setActiveSource(key)}
              className={`shrink-0 inline-flex items-center gap-2 px-3 py-1.5 text-xs font-semibold rounded-full border transition ${
                active
                  ? "bg-emerald-600 border-emerald-600 text-white shadow-sm"
                  : "bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300"
              }`}
            >
              {meta && <span className={`w-1.5 h-1.5 rounded-full ${meta.dot}`} />}
              <span>{key === "all" ? "All" : meta?.short}</span>
              <span className={`px-1.5 py-0.5 text-[10px] rounded-full ${active ? "bg-white/20" : "bg-gray-100 dark:bg-gray-700"}`}>
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-800 dark:text-red-300 rounded-md flex items-start gap-2">
          <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" />
          <div className="flex-1 text-sm">{error}</div>
          <button onClick={() => setError(null)}><X className="w-4 h-4" /></button>
        </div>
      )}

      {visible.length === 0 ? (
        <EmptyState filtered={activeSource !== "all" && orders.length > 0} />
      ) : (
        <div className="space-y-3">
          {visible.map((o) => (
            <OrderCard
              key={o.id}
              order={o}
              isExiting={exiting.has(o.id)}
              isActing={actingId === o.id}
              onConfirm={() => setModal({ type: "confirm", order: o })}
              onPropose={() => setModal({ type: "propose", order: o })}
              onHold={() => setModal({ type: "hold", order: o })}
            />
          ))}
        </div>
      )}

      {modal && (
        <ScheduleModal
          type={modal.type}
          order={modal.order}
          submitting={actingId === modal.order.id}
          onClose={() => setModal(null)}
          onSubmit={async (payload) => {
            if (!token) return;
            setActingId(modal.order.id);
            try {
              const path =
                modal.type === "confirm"
                  ? `/api/ops/work-orders/${modal.order.id}/confirm-schedule`
                  : modal.type === "propose"
                  ? `/api/ops/work-orders/${modal.order.id}/propose-reschedule`
                  : `/api/ops/work-orders/${modal.order.id}/hold`;
              const res = await apiFetch(path, {
                method: "POST",
                headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
                body: JSON.stringify(payload),
              });
              if (!res.ok) {
                const data = await res.json().catch(() => ({}));
                throw new Error(data.message || `Failed (${res.status})`);
              }
              removeOrderWithAnimation(modal.order.id);
              setModal(null);
              setToast({
                tone: "success",
                text:
                  modal.type === "confirm"
                    ? "Schedule confirmed. Customer notified."
                    : modal.type === "propose"
                    ? "New window proposed. Customer notified."
                    : "On hold. Rodo & sales notified.",
              });
            } catch (e: any) {
              setToast({ tone: "error", text: e?.message || "Action failed" });
            } finally {
              setActingId(null);
            }
          }}
        />
      )}

      {toast && (
        <div
          className={`fixed bottom-5 left-1/2 -translate-x-1/2 z-50 px-4 py-3 rounded-lg shadow-lg flex items-center gap-2 text-sm font-medium ${
            toast.tone === "success"
              ? "bg-emerald-600 text-white"
              : toast.tone === "error"
              ? "bg-red-600 text-white"
              : "bg-gray-900 text-white"
          }`}
        >
          {toast.tone === "success" && <Check className="w-4 h-4" />}
          {toast.tone === "error" && <AlertTriangle className="w-4 h-4" />}
          {toast.text}
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, tone }: { label: string; value: number | string; tone: StatTone }) {
  const colors: Record<StatTone, string> = {
    emerald: "text-emerald-700 dark:text-emerald-400",
    red: "text-red-700 dark:text-red-400",
    amber: "text-amber-700 dark:text-amber-400",
    gray: "text-gray-600 dark:text-gray-300",
  };
  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-3">
      <div className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400 font-semibold">{label}</div>
      <div className={`text-2xl font-bold mt-0.5 ${colors[tone]}`}>{value}</div>
    </div>
  );
}

function EmptyState({ filtered }: { filtered: boolean }) {
  return (
    <div className="text-center py-16 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
      <div className="w-14 h-14 mx-auto rounded-full bg-emerald-50 dark:bg-emerald-900/30 flex items-center justify-center mb-3">
        <Sparkles className="w-7 h-7 text-emerald-500" />
      </div>
      <p className="text-base font-semibold text-gray-900 dark:text-gray-100">
        {filtered ? "Nothing in this filter." : `${timeOfDayGreeting()} All caught up.`}
      </p>
      <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
        {filtered ? "Try another source." : "Paid orders will land here for schedule confirmation."}
      </p>
    </div>
  );
}

function OrderCard({
  order: o,
  isExiting,
  isActing,
  onConfirm,
  onPropose,
  onHold,
}: {
  order: IntakeOrder;
  isExiting: boolean;
  isActing: boolean;
  onConfirm: () => void;
  onPropose: () => void;
  onHold: () => void;
}) {
  const source = SOURCE_META[o.source_channel || "cd"] || SOURCE_META.cd;
  const countdown = formatCountdown(o.seconds_remaining);
  const isUrgent = countdown.tone === "danger";
  const orderRef = o.source_order_number || o.wo_number;
  const isDelivery = (o.fulfillment_method || (o.needs_transportation ? "delivery" : "pickup")) === "delivery";
  const isRescheduleReq = o.status === "reschedule_requested";

  const countdownColor =
    countdown.tone === "danger"
      ? "text-red-700 dark:text-red-300"
      : countdown.tone === "warning"
      ? "text-amber-700 dark:text-amber-300"
      : "text-emerald-700 dark:text-emerald-300";

  return (
    <div
      className={`bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 border-l-4 ${source.border} rounded-lg p-4 md:p-5 transition ${
        isExiting ? "opacity-0 translate-x-6" : ""
      } ${isUrgent ? "ring-1 ring-red-300/60" : ""}`}
    >
      <div className="flex flex-col md:flex-row md:items-start gap-4">
        <div className="flex items-center gap-3 md:flex-col md:items-center md:w-16 shrink-0">
          <div className="relative">
            <ProgressRing seconds={o.seconds_remaining} tone={countdown.tone} />
            <div className={`absolute inset-0 flex items-center justify-center text-[11px] font-mono font-semibold ${countdownColor}`}>
              {o.is_overdue ? "!" : countdown.text}
            </div>
          </div>
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1.5 flex-wrap">
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold rounded ${source.bg} ${source.text}`}>
              <span className={`w-1.5 h-1.5 rounded-full ${source.dot}`} />
              {source.label}
            </span>
            <span className="text-xs font-mono text-gray-500">{orderRef}</span>
            <span className="text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
              {isDelivery ? "Delivery" : "Pickup"}
            </span>
            {isRescheduleReq && (
              <span className="text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded bg-indigo-100 text-indigo-700">
                Customer reschedule
              </span>
            )}
            {(o.priority === "high" || o.priority === "urgent") && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold rounded bg-red-100 text-red-700">
                <Zap className="w-3 h-3" />
                {o.priority.toUpperCase()}
              </span>
            )}
          </div>

          <h3 className="text-base md:text-lg font-semibold text-gray-900 dark:text-gray-50">{o.client_name || "—"}</h3>

          {o.line_items.length > 0 ? (
            <ul className="mt-2 text-sm text-gray-700 dark:text-gray-300 space-y-0.5">
              {o.line_items.map((li, idx) => (
                <li key={idx}>
                  <span className="text-emerald-700 dark:text-emerald-400 font-semibold">{li.quantity}</span>{" "}
                  {li.quantity_type} · <span className="font-medium">{li.product_name}</span>
                </li>
              ))}
            </ul>
          ) : (
            <div className="mt-2 text-sm">
              <span className="text-emerald-700 font-semibold">{o.quantity}</span> {o.quantity_type} · {o.product_name}
            </div>
          )}

          <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500">
            {isDelivery && o.destination_address && (
              <span className="inline-flex items-center gap-1">
                <MapPin className="w-3 h-3" />
                {[o.destination_address, o.destination_city, o.destination_state].filter(Boolean).join(", ")}
              </span>
            )}
            {(o.preferred_delivery_date || o.proposed_delivery_date) && (
              <span className="inline-flex items-center gap-1">
                <CalendarDays className="w-3 h-3" />
                {o.proposed_delivery_date || o.preferred_delivery_date}
                {(o.proposed_delivery_time || o.preferred_delivery_time) &&
                  ` · ${o.proposed_delivery_time || o.preferred_delivery_time}`}
              </span>
            )}
          </div>

          {o.custom_notes && (
            <div className="mt-2 p-2 rounded bg-amber-50 dark:bg-amber-900/20 border-l-2 border-amber-400 text-xs text-amber-800 dark:text-amber-200 flex gap-1.5">
              <StickyNote className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              <span className="whitespace-pre-wrap line-clamp-3">{o.custom_notes}</span>
            </div>
          )}
        </div>

        <div className="flex md:flex-col gap-2 md:w-40 shrink-0">
          <button
            onClick={onConfirm}
            disabled={isActing}
            className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white font-semibold text-sm rounded-md"
          >
            <Check className="w-4 h-4" />
            Confirm
          </button>
          <button
            onClick={onPropose}
            disabled={isActing}
            className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-800 dark:text-gray-100 font-medium text-sm rounded-md"
          >
            <Clock className="w-4 h-4" />
            Propose
          </button>
          <button
            onClick={onHold}
            disabled={isActing}
            className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-amber-800 dark:text-amber-200 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 font-medium text-sm rounded-md"
          >
            <PauseCircle className="w-4 h-4" />
            Hold
          </button>
        </div>
      </div>
    </div>
  );
}

function ScheduleModal({
  type,
  order,
  submitting,
  onClose,
  onSubmit,
}: {
  type: "confirm" | "propose" | "hold";
  order: IntakeOrder;
  submitting: boolean;
  onClose: () => void;
  onSubmit: (payload: Record<string, unknown>) => Promise<void>;
}) {
  const isDelivery =
    (order.fulfillment_method || (order.needs_transportation ? "delivery" : "pickup")) === "delivery";
  const [date, setDate] = useState(
    (order.proposed_delivery_date || order.preferred_delivery_date || "").toString().slice(0, 10)
  );
  const [time, setTime] = useState(order.proposed_delivery_time || order.preferred_delivery_time || "");
  const [address, setAddress] = useState(order.destination_address || "");
  const [city, setCity] = useState(order.destination_city || "");
  const [state, setState] = useState(order.destination_state || "AZ");
  const [zip, setZip] = useState(order.destination_zip || "");
  const [note, setNote] = useState("");
  const [holdReason, setHoldReason] = useState("");

  const title =
    type === "confirm" ? "Confirm schedule" : type === "propose" ? "Propose new schedule" : "Place on hold";

  return (
    <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center bg-black/40 p-0 md:p-4" onClick={onClose}>
      <div
        className="w-full md:max-w-lg bg-white dark:bg-gray-900 rounded-t-2xl md:rounded-2xl shadow-xl p-5 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-4">
          <div>
            <h2 className="text-lg font-bold text-gray-900 dark:text-gray-50">{title}</h2>
            <p className="text-sm text-gray-500">{order.client_name} · {order.source_order_number || order.wo_number}</p>
          </div>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-700"><X className="w-5 h-5" /></button>
        </div>

        {type === "hold" ? (
          <div className="space-y-3">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Internal reason (customer is not told “rejected”)
              <textarea
                value={holdReason}
                onChange={(e) => setHoldReason(e.target.value)}
                rows={3}
                className="mt-1 w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm"
                placeholder="e.g. Waiting on product availability / trucking"
              />
            </label>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <label className="block text-sm font-medium">
                Date
                <input
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  className="mt-1 w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm"
                />
              </label>
              <label className="block text-sm font-medium">
                Window / time
                <input
                  type="text"
                  value={time}
                  onChange={(e) => setTime(e.target.value)}
                  placeholder="e.g. 8–11 AM"
                  className="mt-1 w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm"
                />
              </label>
            </div>

            {isDelivery && (
              <div className="space-y-2">
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Delivery address</p>
                <input
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  placeholder="Street"
                  className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm"
                />
                <div className="grid grid-cols-3 gap-2">
                  <input value={city} onChange={(e) => setCity(e.target.value)} placeholder="City" className="rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm" />
                  <input value={state} onChange={(e) => setState(e.target.value)} placeholder="ST" className="rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm" />
                  <input value={zip} onChange={(e) => setZip(e.target.value)} placeholder="ZIP" className="rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm" />
                </div>
              </div>
            )}

            <label className="block text-sm font-medium">
              Note to customer (optional)
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={2}
                className="mt-1 w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm"
              />
            </label>
          </div>
        )}

        <div className="mt-5 flex gap-2">
          <button onClick={onClose} className="flex-1 px-4 py-2.5 text-sm font-medium rounded-md border border-gray-300 dark:border-gray-600">
            Cancel
          </button>
          <button
            disabled={submitting || (type === "hold" ? !holdReason.trim() : !date)}
            onClick={() => {
              if (type === "hold") return onSubmit({ hold_reason: holdReason.trim() });
              if (type === "propose") {
                return onSubmit({
                  proposed_delivery_date: date,
                  proposed_delivery_time: time || null,
                  schedule_note: note || null,
                  destination_address: isDelivery ? address : undefined,
                  destination_city: isDelivery ? city : undefined,
                  destination_state: isDelivery ? state : undefined,
                  destination_zip: isDelivery ? zip : undefined,
                });
              }
              return onSubmit({
                preferred_delivery_date: date,
                preferred_delivery_time: time || null,
                schedule_note: note || null,
                fulfillment_method: isDelivery ? "delivery" : "pickup",
                destination_address: isDelivery ? address : null,
                destination_city: isDelivery ? city : null,
                destination_state: isDelivery ? state : null,
                destination_zip: isDelivery ? zip : null,
              });
            }}
            className={`flex-1 px-4 py-2.5 text-sm font-semibold rounded-md text-white disabled:opacity-50 ${
              type === "hold" ? "bg-amber-600 hover:bg-amber-700" : "bg-emerald-600 hover:bg-emerald-700"
            }`}
          >
            {submitting ? "Saving…" : type === "confirm" ? "Confirm & notify" : type === "propose" ? "Propose & notify" : "Hold & escalate"}
          </button>
        </div>
      </div>
    </div>
  );
}
